import React from "react";
import { FormikProps } from "formik";
import { DocumentNode, OperationVariables, TypedDocumentNode } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { Response } from "AttendanceForm/Types/types";

interface UseFormSubmissionProps<TFormValues, TPayload = null> {
  map: (form: TFormValues) => TPayload;
  form: FormikProps<TFormValues>;
  query: DocumentNode | TypedDocumentNode<unknown, OperationVariables>;
}

export const useFormSubmission = <TFormValues, TPayload>({
  map,
  form,
  query,
}: UseFormSubmissionProps<TFormValues, TPayload>) => {
  const [mutation, { loading }] = useMutation(query);

  const onSubmit = React.useCallback(
    async (mappedValues: unknown) => {
      const response = await mutation({
        variables: mappedValues,
      }).then((r) => {
        const data = Object.values(r.data)[0];
        return data;
      });
      const result: Response = {
        isSuccess: !response.hasError,
        message: response.messages,
        validationErrors: response.validationErrors,
      };
      return result;
    },
    [mutation],
  );

  const onSubmitCallback = React.useCallback(async () => {
    form.setStatus({ isSubmitted: true });

    const errors = await form.validateForm();

    if (Object.keys(errors).length > 0) return { isSuccess: false, validationErrors: errors };

    const mappedValues = map(form.values);

    const response = await onSubmit(mappedValues);

    if (!response.isSuccess) form.setErrors(response.validationErrors);

    return response as Response;
  }, [form, map, onSubmit]);

  return {
    loading,
    onSubmit: onSubmitCallback,
  };
};
